import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import {
	useLazyGetProductReviewMediaQuery,
	useLazyGetProductReviewsQuery,
	useVoteForProductReviewMutation
} from '../api/product-reviews-api'

import {
	DEFAULT_REVIEW_MEDIA_LIMIT,
	DEFAULT_REVIEWS_LIMIT,
	getReviewsApiSort,
	ReviewListMode,
	ReviewSortFilterMode
} from './constants'
import { findReviewMediaIndex, getReviewMediaItems, getVisibleReviews, mergeReviewsById } from './review-state'
import { ProductReview, ProductReviewsResponse, ProductReviewVariantFilter, ReviewMediaItem } from './types'
import { useDebouncedReviewVote } from './use-debounced-review-vote'
import { useProductReviewsModals } from './use-product-reviews-modals'

type UseProductReviewsParams = {
	productCode: number
	productCodes: number[]
	productImages: string[]
	token?: string
	initialData: ProductReviewsResponse
}

export const useProductReviews = ({
	productCode,
	productCodes,
	productImages,
	token,
	initialData
}: UseProductReviewsParams) => {
	const [getProductReviews] = useLazyGetProductReviewsQuery()
	const [getProductReviewMedia] = useLazyGetProductReviewMediaQuery()
	const [voteForProductReview] = useVoteForProductReviewMutation()
	const [data, setData] = useState(initialData)
	const [listMode, setListMode] = useState<ReviewListMode>('newest')
	const [onlyWithPhoto, setOnlyWithPhoto] = useState(false)
	const [onlyWithVideo, setOnlyWithVideo] = useState(false)
	const [ratingFilter, setRatingFilter] = useState<number[]>([])
	const [variant, setVariant] = useState<ProductReviewVariantFilter>('all')
	const [page, setPage] = useState(1)
	const [isRefreshing, setIsRefreshing] = useState(false)
	const [isReplacingReviews, setIsReplacingReviews] = useState(false)
	const [mediaPage, setMediaPage] = useState(1)
	const [isLoadingMedia, setIsLoadingMedia] = useState(false)
	const latestLoadId = useRef(0)
	const ratingLoadTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
	const clearPendingRatingLoad = useCallback(() => {
		if (!ratingLoadTimer.current) return

		clearTimeout(ratingLoadTimer.current)
		ratingLoadTimer.current = null
	}, [])

	useEffect(() => {
		return clearPendingRatingLoad
	}, [clearPendingRatingLoad])

	useEffect(() => {
		latestLoadId.current += 1
		setData(initialData)
		setIsRefreshing(false)
		setIsReplacingReviews(false)
		setMediaPage(1)
		setIsLoadingMedia(false)
		clearPendingRatingLoad()
	}, [initialData])

	useEffect(() => {
		setListMode('newest')
		setOnlyWithPhoto(false)
		setOnlyWithVideo(false)
		setRatingFilter([])
		setVariant('all')
		setPage(1)
		setMediaPage(1)
		clearPendingRatingLoad()
	}, [productCode])

	const load = useCallback(
		async (
			nextMode: ReviewListMode,
			nextVariant: ProductReviewVariantFilter,
			nextPage: number,
			nextOnlyWithPhoto: boolean,
			nextOnlyWithVideo: boolean,
			nextRatingFilter: number[],
			append = false,
			refreshSummary = false
		) => {
			const loadId = ++latestLoadId.current
			setIsRefreshing(true)
			if (!append) setIsReplacingReviews(true)
			try {
				const response = await getProductReviews({
					productCode,
					productCodes,
					sort: getReviewsApiSort(nextMode),
					variant: nextVariant,
					limit: DEFAULT_REVIEWS_LIMIT,
					mediaLimit: DEFAULT_REVIEW_MEDIA_LIMIT,
					currentPage: nextPage,
					isPhoto: nextOnlyWithPhoto,
					isVideo: nextOnlyWithVideo,
					rating: nextRatingFilter.length ? nextRatingFilter : undefined,
					ratingOrder: nextMode === 'rating-low' ? 'asc' : 'desc',
					productImages,
					token,
					cachedSummary: refreshSummary ? undefined : data.summary
				}).unwrap()
				if (loadId !== latestLoadId.current) return
				if (!append) setMediaPage(1)
				setData(current => {
					if (!append) return response
					const reviews = mergeReviewsById(current.reviews, response.reviews)
					return {
						...response,
						reviews,
						gallery: response.mediaItems?.map(item => item.src) ?? [],
						mediaTotal: response.mediaTotal
					}
				})
			} catch {
				// Reviews are secondary data; retain the last successful response.
			} finally {
				if (loadId === latestLoadId.current) {
					setIsRefreshing(false)
					setIsReplacingReviews(false)
				}
			}
		},
		[data.summary, getProductReviews, productCodes, productCode, productImages, token]
	)

	const changeVariant = (value: ProductReviewVariantFilter) => {
		clearPendingRatingLoad()
		setVariant(value)
		setPage(1)
		void load(listMode, value, 1, onlyWithPhoto, onlyWithVideo, ratingFilter)
	}

	const changeSortFilter = (value: ReviewSortFilterMode) => {
		clearPendingRatingLoad()
		const nextOnlyWithPhoto = value === 'photo'
		const nextOnlyWithVideo = value === 'video'
		const nextMode = value === 'photo' || value === 'video' ? 'newest' : value

		setListMode(nextMode)
		setOnlyWithPhoto(nextOnlyWithPhoto)
		setOnlyWithVideo(nextOnlyWithVideo)
		setPage(1)
		void load(nextMode, variant, 1, nextOnlyWithPhoto, nextOnlyWithVideo, ratingFilter)
	}

	const changeRatingFilter = (value: number[]) => {
		clearPendingRatingLoad()
		const nextMode = value.length ? 'newest' : listMode
		if (value.length) setListMode(nextMode)
		setRatingFilter(value)
		setPage(1)
		ratingLoadTimer.current = setTimeout(() => {
			ratingLoadTimer.current = null
			void load(nextMode, variant, 1, onlyWithPhoto, onlyWithVideo, value)
		}, 500)
	}

	const showMore = () => {
		clearPendingRatingLoad()
		const nextPage = page + 1
		setPage(nextPage)
		void load(listMode, variant, nextPage, onlyWithPhoto, onlyWithVideo, ratingFilter, true)
	}

	const voting = useDebouncedReviewVote({ setData, voteForProductReview })

	const modals = useProductReviewsModals({
		token,
		onVote: voting.request
	})

	const handleSubmitted = (rating: number, submittedReview?: ProductReview) => {
		clearPendingRatingLoad()
		if (submittedReview) {
			setData(current => ({
				...current,
				reviews: [submittedReview, ...current.reviews.filter(review => review.id !== submittedReview.id)]
			}))
		}
		modals.success.open(rating)
		modals.reviewForm.close()
		setPage(1)
		void load(listMode, variant, 1, onlyWithPhoto, onlyWithVideo, ratingFilter, false, true)
	}

	const updateReview = (updatedReview: ProductReview) => {
		setData(current => ({
			...current,
			reviews: current.reviews.map(review =>
				review.id === updatedReview.id ? { ...review, ...updatedReview } : review
			),
			mediaItems: current.mediaItems?.map(item =>
				item.review.id === updatedReview.id || item.reviewId === updatedReview.id
					? { ...item, review: { ...item.review, ...updatedReview } }
					: item
			)
		}))
	}

	const visibleReviews = useMemo(() => getVisibleReviews(data.reviews, listMode), [data.reviews, listMode])

	const reviewMediaItems = useMemo<ReviewMediaItem[]>(() => getReviewMediaItems(data), [data])
	const endpointMediaItems = data.mediaItems ?? []
	const sortFilterValue: ReviewSortFilterMode = onlyWithPhoto ? 'photo' : onlyWithVideo ? 'video' : listMode
	const ratingFilterValue = ratingFilter.map(String)

	const openReviewMedia = (reviewId: string, photoIndex: number, src?: string) => {
		const index = findReviewMediaIndex(reviewMediaItems, reviewId, photoIndex, src)
		if (index >= 0) modals.media.open(index)
	}

	const loadMoreMedia = useCallback(async () => {
		clearPendingRatingLoad()
		const loadedMediaCount = data.mediaItems?.length ?? reviewMediaItems.length
		const mediaTotal = data.mediaTotal ?? loadedMediaCount
		if (isLoadingMedia || loadedMediaCount >= mediaTotal) return

		const nextPage = mediaPage + 1
		setIsLoadingMedia(true)
		try {
			const response = await getProductReviewMedia({
				productCode,
				productCodes,
				sort: getReviewsApiSort(listMode),
				variant,
				mediaLimit: DEFAULT_REVIEW_MEDIA_LIMIT,
				currentPage: nextPage,
				isPhoto: onlyWithPhoto,
				isVideo: onlyWithVideo,
				rating: ratingFilter.length ? ratingFilter : undefined,
				ratingOrder: listMode === 'rating-low' ? 'asc' : 'desc',
				token
			}).unwrap()

			setMediaPage(response.currentPage || nextPage)
			setData(current => {
				const currentMediaItems = current.mediaItems ?? []
				const mediaItems = Array.from(
					new Map([...currentMediaItems, ...response.items].map(item => [item.id, item])).values()
				)

				return {
					...current,
					mediaItems,
					gallery: mediaItems.map(item => item.src),
					mediaTotal: response.total
				}
			})
		} catch {
			// Media pagination is secondary; keep already loaded media.
		} finally {
			setIsLoadingMedia(false)
		}
	}, [
		data.mediaItems,
		data.mediaTotal,
		getProductReviewMedia,
		isLoadingMedia,
		listMode,
		mediaPage,
		onlyWithPhoto,
		onlyWithVideo,
		productCodes,
		productCode,
		ratingFilter,
		reviewMediaItems,
		token,
		variant
	])

	return {
		reviews: { data, visible: visibleReviews, isRefreshing, isReplacing: isReplacingReviews },
		filters: {
			variant,
			changeVariant,
			changeSortFilter,
			changeRatingFilter,
			showMore,
			sortFilterValue,
			ratingFilterValue
		},
		voting: { reviewId: voting.reviewId, request: modals.requestVote },
		media: {
			items: endpointMediaItems,
			total: data.mediaTotal ?? endpointMediaItems.length,
			isLoadingMore: isLoadingMedia,
			loadMore: loadMoreMedia,
			openReview: openReviewMedia,
			modal: modals.media
		},
		reviewForm: { ...modals.reviewForm, handleSubmitted, updateReview },
		authorization: modals.authorization,
		moderation: modals.moderation,
		success: modals.success
	}
}
